// ADR 16.10.26b §3 — iOS gets a guided modal, not a steps card.
//
// iOS has no programmatic install (the iOS ceiling, `installAffordance.ts`):
// Share → Add to Home Screen → Add is the only path. What this can fix is how
// that path is presented — a real modal (`ui/surfaces/Dialog.tsx`: a backdrop
// tap, Escape and the close-watcher all close it, focus returns to the
// trigger), one sentence, and a picture of Share.
//
// The sentence names every tap in order and never *where* on screen: an
// unknown shell's Share could be anywhere.
//
// ADR 16.10.26f §5 — now the **fallback**: `guide/InstallGuide.tsx` mounts it
// only for an iOS variant absent from `GUIDES`, which since 16.10.26g is
// `ios_other` alone — an in-app or unknown WebKit shell. Every named browser
// gets the stepper, whose animation carries the *where*, so this dialog says
// `pwa_ios_install_guide_other`'s one sentence and the per-browser ones are
// retired (16.10.26g §3).
//
// Must not import the wasm facade: it is in 25.9.26f's boot graph.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../ui/Button'
import { Dialog } from '../ui/surfaces/Dialog'
import { markStepsShown } from './installPrompt'

/** Kept out of `ui/icons/`, whose set is pinned at 16: a square with an up arrow. */
function ShareGlyph() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="me-1 inline-block h-[1.1em] w-[1.1em] align-text-bottom"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M8 11H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-2" />
    </svg>
  )
}

export function IosInstallDialog({ onClose }: { onClose(): void }) {
  const { t } = useTranslation()
  // The same `appinstalled` attribution contract as `InstallSteps`.
  useEffect(() => markStepsShown('ios'), [])
  return (
    <Dialog
      title={t('pwa_ios_install_title')}
      onClose={onClose}
      actions={
        <Button variant="primary" onClick={onClose}>
          {t('pwa_install_guide_done')}
        </Button>
      }
    >
      <p>
        <ShareGlyph />
        {t('pwa_ios_install_guide_other')}
      </p>
    </Dialog>
  )
}
